// Drawer lateral con el expediente completo de un paciente.

import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Activity, AlertTriangle, Calendar, Pill, ShieldAlert, X } from 'lucide-react';

import { useModules } from '@/context/ModulesContext';
import { getPatientRecord } from '../api';
import { cockpitLabels } from '../labels';
import type { PatientFull } from '../types';

const SEVERITY_CLASS: Record<string, string> = {
  anafilaxia: 'bg-destructive/15 text-destructive',
  severa: 'bg-destructive/10 text-destructive',
  moderada: 'bg-amber-500/15 text-amber-400',
  leve: 'bg-muted text-muted-foreground',
};

export function RecordDrawer({ patientId, onClose }: { patientId: string; onClose: () => void }) {
  const { archetype } = useModules();
  const L = cockpitLabels(archetype);
  const [data, setData] = useState<PatientFull | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    getPatientRecord(patientId)
      .then(setData)
      .catch((e) => setError(String(e?.message || e)))
      .finally(() => setLoading(false));
  }, [patientId]);

  // Cierre con Escape + bloqueo de scroll del body mientras el expediente
  // está abierto (UX de modal de producción).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose]);

  // Portal a document.body: el shell envuelve el contenido con backdrop-filter,
  // y filter/transform en un ancestro rompe `position: fixed` (lo ancla a ese
  // ancestro en vez del viewport) → el drawer se encimaba sobre la lista. El
  // portal lo saca de ese contexto y vuelve a anclarse al viewport.
  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex justify-end bg-black/60 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-2xl overflow-y-auto bg-card border-l border-border shadow-2xl animate-in slide-in-from-right duration-200"
      >
        <header className="sticky top-0 z-10 flex items-center justify-between border-b border-border bg-card px-4 py-3">
          <div className="min-w-0">
            <div className="text-xs uppercase text-muted-foreground">{L.recordLabel}</div>
            {data ? (
              <h2 className="truncate text-lg font-semibold text-foreground">
                {data.display_name}
                {data.age !== null && <span className="ml-2 text-sm font-normal text-muted-foreground">· {data.age}a · {data.sex || '?'}</span>}
              </h2>
            ) : (
              <h2 className="text-lg font-semibold text-muted-foreground">Cargando…</h2>
            )}
          </div>
          <button
            onClick={onClose}
            className="rounded p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
            aria-label="Cerrar"
          >
            <X size={20} />
          </button>
        </header>

        <div className="space-y-4 p-4">
          {loading && <div className="text-sm text-muted-foreground">{L.recordLoading}</div>}
          {error && <div className="text-sm text-destructive">Error: {error}</div>}

          {data && (
            <>
              {/* Red flags */}
              {data.red_flags.length > 0 && (
                <Section title="Banderas rojas" icon={<AlertTriangle size={16} className="text-amber-400" />}>
                  <ul className="space-y-1">
                    {data.red_flags.map((f, i) => (
                      <li key={i} className="rounded border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-sm text-foreground">
                        {f}
                      </li>
                    ))}
                  </ul>
                </Section>
              )}

              {/* Summary */}
              <Section title="Resumen">
                <p className="text-sm leading-relaxed text-foreground">{data.summary_text}</p>
              </Section>

              {/* Alergias */}
              <Section title="Alergias" icon={<ShieldAlert size={16} className="text-muted-foreground" />}>
                {data.allergies.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No registradas.</p>
                ) : (
                  <ul className="space-y-2">
                    {data.allergies.map((a, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm">
                        <span className={`rounded-full px-2 py-0.5 text-xs ${SEVERITY_CLASS[a.severity] || ''}`}>
                          {a.severity}
                        </span>
                        <div>
                          <div className="font-medium text-foreground">{a.substance}</div>
                          {a.reaction && <div className="text-xs text-muted-foreground">{a.reaction}</div>}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </Section>

              {/* Condiciones */}
              <Section title="Condiciones activas" icon={<Activity size={16} className="text-muted-foreground" />}>
                {data.active_conditions.length === 0 ? (
                  <p className="text-sm text-muted-foreground">Ninguna confirmada.</p>
                ) : (
                  <ul className="space-y-1.5">
                    {data.active_conditions.map((c, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm">
                        <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] font-mono uppercase text-muted-foreground">
                          {c.cie10_code || '—'}
                        </span>
                        <span className="text-foreground">{c.display}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </Section>

              {/* Medicaciones */}
              <Section title="Medicamentos activos" icon={<Pill size={16} className="text-muted-foreground" />}>
                {data.active_medications.length === 0 ? (
                  <p className="text-sm text-muted-foreground">Ninguno activo.</p>
                ) : (
                  <ul className="space-y-1.5">
                    {data.active_medications.map((m, i) => (
                      <li key={i} className="text-sm text-foreground">
                        <span className="font-medium">{m.name}</span>
                        {m.dose && <span> · {m.dose}</span>}
                        {m.frequency && <span className="text-muted-foreground"> · {m.frequency}</span>}
                        {m.indication && <span className="text-xs text-muted-foreground"> · {m.indication}</span>}
                      </li>
                    ))}
                  </ul>
                )}
              </Section>

              {/* Observaciones recientes */}
              {data.recent_observations.length > 0 && (
                <Section title="Observaciones recientes">
                  <ul className="space-y-1">
                    {data.recent_observations.slice(0, 6).map((o, i) => (
                      <li key={i} className="flex justify-between text-sm">
                        <span className="text-foreground">{o.display}</span>
                        <span className="text-muted-foreground">
                          {o.value}{o.unit && ` ${o.unit}`} ·{' '}
                          {new Date(o.observed_at).toLocaleDateString('es-MX')}
                        </span>
                      </li>
                    ))}
                  </ul>
                </Section>
              )}

              {/* Encuentros */}
              <Section title="Encuentros recientes" icon={<Calendar size={16} className="text-muted-foreground" />}>
                {data.encounters.length === 0 ? (
                  <p className="text-sm text-muted-foreground">Sin encuentros registrados.</p>
                ) : (
                  <ul className="space-y-1.5">
                    {data.encounters.map((e) => (
                      <li key={e.id} className="text-sm">
                        <span className="font-medium text-foreground">{e.type}</span>
                        <span className="ml-2 text-xs text-muted-foreground">
                          {new Date(e.scheduled_at).toLocaleDateString('es-MX')} · {e.status}
                        </span>
                        {e.motivo && <div className="text-xs text-muted-foreground">{e.motivo}</div>}
                      </li>
                    ))}
                  </ul>
                )}
              </Section>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}

function Section({ title, icon, children }: {
  title: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section>
      <h3 className="mb-2 flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {icon}{title}
      </h3>
      {children}
    </section>
  );
}
